// apps/web/src/features/clients/components/CopyTextButton.tsx
//
// One copy affordance for the client workspace. Owns its own clipboard
// outcome so a refusal surfaces the selectable fallback NEXT TO the control
// that failed, not at the top of the page (AC-6).
//
// INVARIANT: `text` is passed through verbatim — the caller hands over the
// record's original `details` / `inn` / `phone`, never a re-flowed preview.
import { Button, IconButton } from '../../../ui/index.js';
import { useCopyText } from '../useCopyText.js';
import { useActionSize, useTouchActionStyle } from '../useMobileFieldStyle.js';
import { CopyFallback } from './CopyFallback.js';

export interface CopyTextButtonProps {
  text: string;
  /** Accessible label naming WHAT is copied, e.g. the INN of a named entity. */
  label: string;
  /** Visible label; when omitted the control renders as an icon button. */
  children?: string;
  testId?: string;
}

export function CopyTextButton({ text, label, children, testId }: CopyTextButtonProps) {
  const { copy, failedText, dismissFailure } = useCopyText();
  const actionSize = useActionSize();
  // The labelled variant is the PRIMARY copy action on a requisites card, so it
  // carries the same touch minimum as the icon-only one — `size="sm"` alone
  // leaves it at 28 px tall (AC-6/AC-12).
  const actionStyle = useTouchActionStyle();
  return (
    <>
      {children === undefined ? (
        <IconButton
          icon="copy"
          size={actionSize}
          label={label}
          onClick={() => void copy(text)}
          {...(testId ? { 'data-testid': testId } : {})}
        />
      ) : (
        <Button
          size="sm"
          icon="copy"
          style={actionStyle}
          aria-label={label}
          onClick={() => void copy(text)}
          {...(testId ? { 'data-testid': testId } : {})}
        >
          {children}
        </Button>
      )}
      {failedText !== null && (
        <div style={{ flexBasis: '100%', minWidth: 0 }}>
          <CopyFallback text={failedText} onDismiss={dismissFailure} />
        </div>
      )}
    </>
  );
}
